import { Block } from '@mui/icons-material';
import { Avatar, IconButton, LinearProgress, MenuItem, Tooltip, Typography, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { ChevronDown, HomeIcon, LogOutIcon, PanelLeftClose, Settings, UserRoundSearch } from 'lucide-react';
import * as React from 'react';
import { Suspense } from 'react';
import { useDispatch } from 'react-redux-latest';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Notification } from '../../components/atoms/notification';
import BlockList from '../../components/blocklist/block-list';
import { MenuContainer } from '../../components/organisms/TopBar/containers';
import { PageContainer, UnderTopBarContainer } from '../containers';
// import { AccountAvatar } from '../../components/organisms/TopBar/elements';
import { useMsal } from '@azure/msal-react';
import { BuildInfo, getZaplifySdk } from '@zaplify/sdk';
import { LocalStorageKeys } from '../../config';
import {
    clearLocalStorageKeys,
    displayBlocklistModal,
    logoutError,
    setNotification,
    shouldPreventRedirectToLogin,
} from '../../redux/actions';
import { AppDispatch, useAppSelector } from '../../redux/store/configureStore';
import { paths } from '../../routes/paths';
import EnsureAuthenticated from '../ensureAuthenticated';

// Type for dropdown menu items
interface DropdownItem {
    text: string;
    icon: React.ReactNode;
    name: string;
    visible: boolean;
    onClick: () => void;
    additional?: React.ReactNode;
}

const InAppTemplate = (_props: unknown) => {
    const dispatch = useDispatch<AppDispatch>();
    const notification = useAppSelector((state) => state.ui.notification);

    const [openSidebar, setOpenSidebar] = React.useState(!useMediaQuery('(max-width: 1390px)'));
    const handleChangeSidebarState = () => {
        setOpenSidebar((state) => !state);
    };

    React.useEffect(() => {
        const handleResize = () => {
            const isSmallScreen = window.innerWidth <= 1390;
            setOpenSidebar(!isSmallScreen);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <EnsureAuthenticated>
            <Suspense fallback={<LinearProgress />}>
                <PageContainer>
                    <ResponsiveDrawer openSidebar={openSidebar} handleChangeSidebarState={handleChangeSidebarState} />
                    <Box
                        sx={{
                            borderRadius: 'var(--radius-lg, 14px)',
                            background: 'var(--grey-palette-white, #FFF)',
                            boxShadow:
                                '0px 4px 12px 0px rgba(30, 36, 48, 0.08), 0px 1px 3px 0px rgba(30, 36, 48, 0.10)',
                            marginTop: '4px',
                            marginBottom: '4px',
                            minHeight: 'calc(100vh - 8px)',
                            boxSizing: 'border-box',
                            width: openSidebar ? 'calc(100vw - 240px)' : 'calc(100vw - 48px)',
                            marginRight: '4px',
                            overflow: 'scroll',
                            padding: '24px',
                            border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                        }}
                    >
                        <UnderTopBarContainer>
                            <Outlet />
                        </UnderTopBarContainer>
                    </Box>
                    {notification.message && (
                        <Notification
                            text={notification.message}
                            onClose={() => dispatch(setNotification('', ''))}
                            severity={notification.severity}
                            vertical="bottom"
                            horizontal="left"
                        />
                    )}
                    <BlockList positionAtTop />
                </PageContainer>
            </Suspense>
        </EnsureAuthenticated>
    );
};

export default InAppTemplate;

export const ResponsiveDrawer = ({ openSidebar, handleChangeSidebarState }) => {
    const location = useLocation();
    const path = location.pathname;

    const drawer = (
        <Box
            className={`sidebar ${openSidebar ? 'open' : 'closed'}`}
            sx={{
                display: 'flex',
                padding: 'var(--S-s-regular, 8px)',
                flexDirection: 'column',
                gap: 'var(--S-s-spacious, 12px)',
                height: '100%',
                '&.open': {
                    maxWidth: '250px',
                },
                '& .MuiTypography-root': {
                    overflow: 'hidden',
                    color: 'var(--grey-palette-grey-5, #3A4B59)',
                    textOverflow: 'ellipsis',
                    fontSize: '14px',
                    fontWeight: 500,
                },
                '& .MuiListItemIcon-root': {
                    minWidth: '0',
                    marginRight: '6px',
                },
                '& ul > a': {
                    textDecoration: 'none',
                    marginBottom: '4px',
                },
                '& ul': {
                    paddingLeft: 0,
                    hieght: '100%',
                    '& li > div': {
                        paddingLeft: '16px',
                    },
                },
                '& hr': {
                    borderColor: '#E4E6EB',
                },
                '& .section-title': {
                    color: 'var(--grey-palette-grey-4, #6B7985)',
                    fontSize: 12,
                    fontWeight: 500,
                    marginLeft: '8px',
                },
                '& .MuiListItemButton-root': {
                    borderRadius: 'var(--radius-md, 8px)',
                    transition: '0.1s',
                    '&:hover': {
                        background: 'var(--primary-20-extra-light, rgba(106, 118, 251, 0.10))',
                    },
                    '&:active': {
                        background: 'var(--primary-20-light, rgba(106, 118, 251, 0.20))',
                    },
                },
                '& a.active > .MuiListItemButton-root': {
                    background: 'var(--primary-20-light, rgba(106, 118, 251, 0.20))',
                },
                '& .MuiListItemText-root': {
                    margin: 0,
                },
                '& .credits': {
                    '& > li ': {
                        paddingLeft: 0,
                        '& > div': {
                            paddingLeft: '16px',
                        },
                    },
                },
                '&.closed': {
                    width: '48px',
                    boxSizing: 'border-box',
                    '& .MuiListItemButton-root': {
                        justifyContent: 'center',
                        width: '100%',
                        padding: '8px',
                    },
                    '& .MuiListItemIcon-root': {
                        marginRight: 0,
                    },
                    '& .MuiListItemText-root': {
                        display: 'none',
                    },
                },
            }}
        >
            <AccountDropdown handleChangeSidebarState={handleChangeSidebarState} sideBarIsOpen={openSidebar} />
            <List>
                <ListItem
                    key="Campaigns"
                    disablePadding
                    component={Link}
                    to={paths.PROSPECT_COLLECTIONS}
                    className={path === paths.PROSPECT_COLLECTIONS ? 'active' : 'inactive'}
                >
                    <Tooltip title={openSidebar ? '' : 'Home'} placement="right">
                        <ListItemButton>
                            <ListItemIcon>
                                <HomeIcon size={20} strokeWidth="1px" />
                            </ListItemIcon>
                            <ListItemText primary={'Home'} />
                        </ListItemButton>
                    </Tooltip>
                </ListItem>
                <ListItem
                    component={Link}
                    to={paths.PROSPECTING}
                    key="Prospecting"
                    disablePadding
                    className={path.includes(paths.PROSPECTING) ? 'active' : 'inactive'}
                >
                    <Tooltip title={openSidebar ? '' : 'Prospects'} placement="right">
                        <ListItemButton>
                            <ListItemIcon>
                                <UserRoundSearch size={20} strokeWidth="1px" />
                            </ListItemIcon>
                            <ListItemText primary={'Prospecting'} />
                        </ListItemButton>
                    </Tooltip>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box
            component="nav"
            sx={{ width: { sm: openSidebar ? '240px' : '48px' }, flexShrink: { sm: 0 } }}
            aria-label="mailbox folders"
        >
            <Drawer
                variant="permanent"
                className={`drawer ${openSidebar ? 'open' : 'closed'}`}
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        backgroundColor: 'transparent',
                        border: 'none',
                    },
                    '&.closed': {
                        '& .MuiDrawer-paper': {
                            width: '48px',
                            boxSizing: 'border-box',
                        },
                    },
                    '&.open': {
                        '& .MuiDrawer-paper': {
                            width: '240px',
                        },
                    },
                }}
                open
            >
                {drawer}
            </Drawer>
        </Box>
    );
};

export const AccountDropdown = ({ handleChangeSidebarState, sideBarIsOpen }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [anchorElement, setAnchorElement] = React.useState(null);
    const user = useAppSelector((state) => state.user.zaplifyUser);
    const msal = useMsal();
    const [buildInfo, setBuildInfo] = React.useState<BuildInfo | null>(null);

    // Fetch build info on mount
    React.useEffect(() => {
        const sdk = getZaplifySdk();
        sdk.getVersion().then(setBuildInfo);
    }, []);

    // Refresh build info (for TTL) when tooltip is shown
    const refreshBuildInfo = () => {
        const sdk = getZaplifySdk();
        sdk.getVersion().then(setBuildInfo);
    };

    // Format UTC timestamp to local time
    const formatBuildTime = (utcTimestamp: string): string => {
        if (!utcTimestamp || utcTimestamp === 'N/A') return 'N/A';
        const date = new Date(utcTimestamp);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
            date.getMinutes()
        )}:${pad(date.getSeconds())}`;
    };

    const dropdownData = [
        {
            text: 'Blocklist',
            icon: <Block data-cy="blocklist-icon" />,
            name: 'blocklist',
            visible: true,
            onClick: () => {
                dispatch(displayBlocklistModal(true));
                setAnchorElement(false);
            },
        },
        {
            text: 'Settings',
            icon: <Settings data-cy="settings-icon" size={20} />,
            name: 'settings',
            visible: true,
            onClick: () => {
                setAnchorElement(false);
                navigate(paths.ACCOUNT_MANAGEMENT + '/subscription');
            },
        },
        {
            text: 'Log out',
            icon: <LogOutIcon data-cy="logout-icon" size={20} />,
            name: 'logout',
            visible: true,
            onClick: () => {
                dispatch(shouldPreventRedirectToLogin(true));
                msal.instance
                    .logoutPopup()
                    .then(() => {
                        localStorage.removeItem(LocalStorageKeys.AUTHENTICATE_AS_USER);
                        clearLocalStorageKeys();
                        navigate('/login');
                    })
                    .catch((_e) => {
                        dispatch(logoutError('error logging out'));
                    });
            },
        },
    ];

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    flexDirection: sideBarIsOpen ? 'row' : 'column',
                }}
            >
                {!sideBarIsOpen && (
                    <Tooltip title={'Open sidebar'} placement="right">
                        <ListItemButton onClick={handleChangeSidebarState}>
                            <ListItemIcon
                                sx={{
                                    '& svg': {
                                        transform: 'rotate(180deg)',
                                        color: '#3A4B59',
                                    },
                                }}
                            >
                                <PanelLeftClose size={20} strokeWidth={1} />
                            </ListItemIcon>
                        </ListItemButton>
                    </Tooltip>
                )}
                <Tooltip title={sideBarIsOpen ? '' : 'Account & settings'} placement="right">
                    <ListItemButton sx={{ paddingLeft: '8px', maxHeight: '37px' }} onClick={(e) => setAnchorElement(e)}>
                        <ListItemIcon>
                            <Avatar
                                variant="rounded"
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    background: '#FE7274',
                                }}
                            >
                                {user.firstName[0]}
                            </Avatar>
                        </ListItemIcon>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--S-xs, 6px)',
                                flex: '0 1 0',
                                '& > div:first-child': {
                                    display: 'block',
                                    '-webkit-flex': 'none',
                                },
                            }}
                        >
                            <ListItemText
                                primary={`${user.firstName}`}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 90,
                                }}
                            />
                        </Box>
                        <ChevronDown size={16} strokeWidth={1} />
                    </ListItemButton>
                </Tooltip>
                {sideBarIsOpen && (
                    <IconButton onClick={handleChangeSidebarState}>
                        <PanelLeftClose size={20} strokeWidth={1} />
                    </IconButton>
                )}
            </Box>

            <MenuContainer
                open={Boolean(anchorElement)}
                onClose={() => setAnchorElement(null)}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                sx={{ marginTop: sideBarIsOpen ? '40px' : '60px', marginLeft: '-8px' }}
                anchorPosition={{ left: 8 }}
                className="no-margin"
            >
                <div className="user-data-container">
                    <Avatar className="avatar">{user.firstName[0]}</Avatar>
                    <div className="user-data">
                        <Typography className="name">{`${user.firstName} ${user.lastName}`}</Typography>
                        <Typography className="company">{user.company}</Typography>
                    </div>
                </div>
                <Divider className="divider" />
                {dropdownData.map((el: DropdownItem) => {
                    return (
                        el.visible && (
                            <div key={el.name}>
                                {el.additional && el.additional}
                                <MenuItem onClick={el.onClick} className="menu-item bar">
                                    <ListItemIcon className={`icon ${el.name}`}>{el.icon}</ListItemIcon>
                                    <ListItemText primary={el.text} className="text" />
                                </MenuItem>
                            </div>
                        )
                    );
                })}
                <Box className="footer-row">
                    {buildInfo && (
                        <Tooltip
                            title={`Index: ${buildInfo.elasticsearch?.indexName || 'N/A'} (TTL: ${
                                buildInfo.elasticsearch?.ttlSeconds ?? 'N/A'
                            }s)`}
                            placement="top"
                            onOpen={refreshBuildInfo}
                        >
                            <Typography className="build-info">
                                #{buildInfo.buildNumber} • {buildInfo.gitHash?.slice(0, 7) || 'N/A'} •{' '}
                                {formatBuildTime(buildInfo.buildTimestamp)}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            </MenuContainer>
        </>
    );
};
